import { defineStore } from "pinia";
import { toast } from "@/utils/toast";
import loansService from "@/services/hrm/loans.service";

const errMsg = (e) =>
  e?.response?.data?.message || e?.message || "Unexpected error";

export const useLoansStore = defineStore("loans", {
  state: () => ({
    rows: [],
    total: 0,
    pageSize: 25,
    current: 1,
    loading: false,
    currentLoan: null,
    lastParams: {},
  }),

  actions: {
    async fetch(params = {}) {
      this.loading = true;
      this.lastParams = { ...params };
      try {
        const { data } = await loansService.list(params);

        if (Array.isArray(data)) {
          this.rows = data;
          this.total = data.length;
          this.pageSize = params.per_page || 25;
          this.current = params.page || 1;
        } else {
          this.rows = data.data || [];
          this.total = data.total || 0;
          this.pageSize = data.per_page || params.per_page || 25;
          this.current = data.current_page || params.page || 1;
        }

        return data;
      } catch (e) {
        toast(errMsg(e), "error");
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async refresh() {
      return this.fetch(this.lastParams || {});
    },

    async create(payload) {
      try {
        const { data } = await loansService.create(payload);
        toast("Loan created");
        await this.refresh();
        return data;
      } catch (e) {
        toast(errMsg(e), "error");
        throw e;
      }
    },

    async show(id) {
      try {
        const { data } = await loansService.show(id);
        this.currentLoan = data;
        return data;
      } catch (e) {
        toast(errMsg(e), "error");
        throw e;
      }
    },

    async update(id, payload) {
      try {
        const { data } = await loansService.update(id, payload);
        toast("Loan updated");
        await this.refresh();
        return data;
      } catch (e) {
        toast(errMsg(e), "error");
        throw e;
      }
    },

    async destroy(id) {
      try {
        await loansService.destroy(id);
        toast("Loan deleted");
        await this.refresh();
      } catch (e) {
        toast(errMsg(e), "error");
        throw e;
      }
    },

    async payoffPreview(id, params = {}) {
      const { data } = await loansService.payoffPreview(id, params);
      return data;
    },

    async settleOnNextPayroll(id, payload = {}) {
      try {
        const { data } = await loansService.settleOnNextPayroll(id, payload);
        toast(data?.message || "Loan ready for full settlement on payroll.");
        await this.refresh();
        return data;
      } catch (e) {
        toast(errMsg(e), "error");
        throw e;
      }
    },
  },
});
